import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import HistoryIcon from "@mui/icons-material/History";
import { useAuth } from "../../../context/AuthContext";
import {
  getInventarioAccesorios,
  moverAccesorioStock,
  getHistorialAccesorio,
} from "../../registroEquipos/services/registroEquiposService";
import { CIUDADES, normalizeCiudad } from "../../registroEquipos/components/EquipoSerializadoForm";

const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
  "& .MuiSelect-select": { py: 0.35, px: 1 },
};

const COLUMNAS = [
  ["almacen", "Almacen"],
  ["buen_estado", "Laboratorio"],
  ["mantenimiento", "Mantenimiento"],
  ["baja", "Baja"],
];

const initialMover = { origen: "almacen", destino: "buen_estado", cantidad: "", observacion: "" };

// Control de inventario de accesorios por ciudad, igual que el reporte viejo (Tipo |
// Almacen | Laboratorio | Mantenimiento | Baja | Total). La lista viene del catálogo
// completo (incluye modelos sin stock todavía, en 0); solo se puede mover stock en una
// fila que ya tiene equipo_accesorio.id — para las que están en 0 sin fila, hay que usar
// "Registrar Accesorio" en /registro-equipos (eso crea la fila).
//
// El total por fila es fijo: acá NO se puede aumentar cantidad, solo mover entre las 4
// categorías (Almacén/Laboratorio/Mantenimiento/Baja) — sumar stock nuevo es exclusivo de
// "Registrar Accesorio".
export default function InventarioAccesorios() {
  const { user } = useAuth();
  const [ciudad, setCiudad] = useState(normalizeCiudad(user?.sucursal) || CIUDADES[0]);
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [buscar, setBuscar] = useState("");
  const [moverDialog, setMoverDialog] = useState(null);
  const [valoresMover, setValoresMover] = useState(initialMover);
  const [guardando, setGuardando] = useState(false);
  const [errorDialog, setErrorDialog] = useState("");
  const [historialDialog, setHistorialDialog] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [errorHistorial, setErrorHistorial] = useState("");

  useEffect(() => {
    if (!user?.sucursal) return;
    setCiudad((prev) => prev || normalizeCiudad(user.sucursal) || CIUDADES[0]);
  }, [user?.sucursal]);

  const cargar = () => {
    if (!ciudad) return;
    setLoading(true);
    setError("");
    getInventarioAccesorios(ciudad)
      .then(({ data }) => setFilas(Array.isArray(data) ? data : []))
      .catch((e) => setError(e?.response?.data?.error || e?.message || "No se pudo cargar el inventario"))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [ciudad]);

  const filasFiltradas = useMemo(() => {
    const needle = buscar.trim().toLowerCase();
    if (!needle) return filas;
    return filas.filter((f) => f.modelo.toLowerCase().includes(needle));
  }, [filas, buscar]);

  const abrirMover = (fila) => {
    setMoverDialog(fila);
    setValoresMover(initialMover);
    setErrorDialog("");
  };

  const destinosDisponibles = COLUMNAS.filter(([key]) => key !== valoresMover.origen);

  const abrirHistorial = (fila) => {
    setHistorialDialog(fila);
    setHistorial([]);
    setErrorHistorial("");
    setCargandoHistorial(true);
    getHistorialAccesorio(fila.id)
      .then(({ data }) => setHistorial(Array.isArray(data) ? data : []))
      .catch((e) => setErrorHistorial(e?.response?.data?.error || e?.message || "No se pudo cargar el historial"))
      .finally(() => setCargandoHistorial(false));
  };

  const mover = async () => {
    if (!moverDialog) return;
    setGuardando(true);
    setErrorDialog("");
    try {
      const { data } = await moverAccesorioStock(moverDialog.id, {
        origen: valoresMover.origen,
        destino: valoresMover.destino,
        cantidad: Number(valoresMover.cantidad),
        observacion: valoresMover.observacion || undefined,
        usu: user?.username,
      });
      setFilas((prev) =>
        prev.map((f) =>
          f.id === moverDialog.id
            ? { ...f, ...data, total: data.almacen + data.buen_estado + data.mantenimiento + data.baja }
            : f
        )
      );
      setMoverDialog((prev) => ({ ...prev, ...data }));
      setValoresMover(initialMover);
    } catch (e) {
      setErrorDialog(e?.response?.data?.error || e?.message || "No se pudo mover el stock");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 0.75 }}>
      <Grid container spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
        <Grid size={{ xs: 12, sm: 4, md: 2.5 }}>
          <FormControl size="small" fullWidth sx={controlSx}>
            <InputLabel>Ciudad</InputLabel>
            <Select label="Ciudad" value={ciudad} onChange={(e) => setCiudad(e.target.value)}>
              {CIUDADES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 8, md: 4 }}>
          <TextField
            size="small"
            label="Buscar modelo"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            fullWidth
            sx={controlSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <Chip size="small" label={`Total: ${filasFiltradas.length}`} sx={{ height: 24, fontSize: 11 }} />
        </Grid>
      </Grid>

      {!!error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 0.75 }} />}

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "calc(100vh - 220px)", borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.3, px: 0.7, fontSize: 11.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Tipo</TableCell>
              {COLUMNAS.map(([key, label]) => (
                <TableCell key={key} sx={headCellSx} align="right">
                  {label}
                </TableCell>
              ))}
              <TableCell sx={headCellSx} align="right">
                Total
              </TableCell>
              <TableCell sx={headCellSx} align="center" width={70}>
                Accion
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!filasFiltradas.length && !loading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Sin resultados.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filasFiltradas.map((f) => (
                <TableRow key={`${f.id_servicio_detalle}-${f.id_equipo}`} hover>
                  <TableCell>{f.modelo}</TableCell>
                  {COLUMNAS.map(([key]) => (
                    <TableCell key={key} align="right">
                      {f[key] < 0 ? (
                        <Chip size="small" label={f[key]} color="warning" sx={{ height: 19, fontSize: 10.5 }} />
                      ) : (
                        f[key]
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {f.total}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {f.id && (
                      <>
                        <IconButton size="small" onClick={() => abrirMover(f)} title="Mover entre categorías" sx={{ p: 0.3 }}>
                          <SwapHorizIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => abrirHistorial(f)} title="Ver historial" sx={{ p: 0.3 }}>
                          <HistoryIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!moverDialog} onClose={() => !guardando && setMoverDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 0.9, px: 1.25, fontSize: 14, fontWeight: 800 }}>
          Mover stock
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
            {moverDialog?.modelo}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1.25 }}>
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {COLUMNAS.map(([key, label]) => (
                <Chip key={key} size="small" label={`${label}: ${moverDialog?.[key] ?? 0}`} variant="outlined" />
              ))}
              <Chip size="small" label={`Total: ${moverDialog?.total ?? 0}`} sx={{ fontWeight: 800 }} />
            </Stack>

            {!!errorDialog && (
              <Alert severity="error" sx={{ fontSize: 12.5 }}>
                {errorDialog}
              </Alert>
            )}

            <FormControl size="small" fullWidth>
              <InputLabel>Origen</InputLabel>
              <Select
                label="Origen"
                value={valoresMover.origen}
                onChange={(e) => {
                  const origen = e.target.value;
                  setValoresMover((prev) => ({
                    ...prev,
                    origen,
                    destino: prev.destino === origen ? COLUMNAS.find(([k]) => k !== origen)[0] : prev.destino,
                  }));
                }}
              >
                {COLUMNAS.map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Destino</InputLabel>
              <Select
                label="Destino"
                value={valoresMover.destino}
                onChange={(e) => setValoresMover((prev) => ({ ...prev, destino: e.target.value }))}
              >
                {destinosDisponibles.map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Cantidad"
              type="number"
              size="small"
              value={valoresMover.cantidad}
              onChange={(e) => setValoresMover((prev) => ({ ...prev, cantidad: e.target.value }))}
              inputProps={{ min: 1 }}
              fullWidth
            />

            <TextField
              label="Observación"
              size="small"
              value={valoresMover.observacion}
              onChange={(e) => setValoresMover((prev) => ({ ...prev, observacion: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 1.25, py: 0.9 }}>
          <Button size="small" onClick={() => setMoverDialog(null)} disabled={guardando}>
            Cerrar
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={mover}
            disabled={guardando || !valoresMover.cantidad || Number(valoresMover.cantidad) <= 0}
            startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Mover
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!historialDialog} onClose={() => setHistorialDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 0.9, px: 1.25, fontSize: 14, fontWeight: 800 }}>
          Historial
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
            {historialDialog?.modelo}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1.25 }}>
          {!!errorHistorial && (
            <Alert severity="error" sx={{ mb: 1, fontSize: 12.5 }}>
              {errorHistorial}
            </Alert>
          )}
          {cargandoHistorial ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : !historial.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              Sin movimientos registrados.
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.3, px: 0.7, fontSize: 11.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Fecha</TableCell>
                    <TableCell sx={headCellSx}>Operación</TableCell>
                    <TableCell sx={headCellSx} align="right">
                      Cantidad
                    </TableCell>
                    <TableCell sx={headCellSx}>Cod OT</TableCell>
                    <TableCell sx={headCellSx}>Ciudad</TableCell>
                    <TableCell sx={headCellSx}>Observación</TableCell>
                    <TableCell sx={headCellSx}>Usuario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historial.map((h) => (
                    <TableRow key={h.id} hover>
                      <TableCell>{String(h.fecha || "").slice(0, 10)}</TableCell>
                      <TableCell>{h.operacion}</TableCell>
                      <TableCell align="right">{h.cantidad}</TableCell>
                      <TableCell>{h.cod_ot || "-"}</TableCell>
                      <TableCell>{h.ciudad || "-"}</TableCell>
                      <TableCell>{h.observacion || "-"}</TableCell>
                      <TableCell>{h.usu}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 1.25, py: 0.9 }}>
          <Button size="small" onClick={() => setHistorialDialog(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
